import React, { useCallback, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { reportsAPI } from '../../../services/api';
import useReport from '../shared/useReport';
import ChartPanel from '../shared/ChartPanel';
import KpiStrip from '../shared/KpiStrip';
import ReportTable from '../shared/ReportTable';
import PrintButton from '../shared/PrintButton';
import DateRangeFilter, { getPresetRange } from '../shared/DateRangeFilter';
import { axisProps, tooltipStyle, barColor } from '../shared/chartTheme';
import { formatHours } from '../../attendance/attendanceStatus';
import { kpiMap } from './kpis';

// How each person's attendance stands over a period. The counts come from the same day rules the
// attendance board shows, so a day cannot read Present here and Absent there.
//
// Attendance % counts a half day as half and approved leave as kept, because leave is agreed
// absence: someone who took their leave properly should not read as unreliable.

const pct = (v) => (v == null ? '-' : `${Number(v).toFixed(1)}%`);

const COLUMNS = [
  { key: 'userName', label: 'Name', wrap: true },
  { key: 'role', label: 'Role' },
  { key: 'workingDays', label: 'Working days', align: 'right' },
  { key: 'present', label: 'Present', align: 'right' },
  { key: 'halfDays', label: 'Half days', align: 'right' },
  { key: 'absent', label: 'Absent', align: 'right' },
  { key: 'onLeave', label: 'On leave', align: 'right' },
  { key: 'lateDays', label: 'Late', align: 'right' },
  {
    key: 'hours', label: 'Hours', align: 'right',
    render: (r) => formatHours(r.hours) || '-',
    csv: (r) => r.hours,
  },
  {
    key: 'averageInTime', label: 'Average in', align: 'right',
    render: (r) => r.averageInTime || '-',
  },
  {
    key: 'attendancePercent', label: 'Attendance', align: 'right',
    render: (r) => (
      <span className={`font-semibold ${r.attendancePercent >= 90 ? 'text-emerald-700' : r.attendancePercent >= 75 ? 'text-amber-700' : 'text-red-700'}`}>
        {pct(r.attendancePercent)}
      </span>
    ),
    csv: (r) => r.attendancePercent,
  },
];

const AttendanceSummaryReport = () => {
  const [range, setRange] = useState(() => getPresetRange('thisMonth'));
  const [preset, setPreset] = useState('thisMonth');

  const fetcher = useCallback(
    () => reportsAPI.getAttendanceSummary({ from: range.from, to: range.to }),
    [range],
  );
  const { data, loading } = useReport(fetcher);

  const rows = useMemo(() => data?.rows || [], [data]);
  const k = kpiMap(data);
  const kpis = [
    { label: 'Staff', value: k.staff ?? rows.length, sub: 'People with working days in the period' },
    { label: 'Average attendance', value: pct(k.averageAttendance), sub: 'Present, counting a half day as half' },
    { label: 'Late days', value: k.lateDays ?? 0, sub: 'Punched in after the grace time' },
    { label: 'Absent days', value: k.absentDays ?? 0, sub: 'Working days with no attendance and no leave', tone: (k.absentDays ?? 0) > 0 ? 'alert' : undefined },
    { label: 'Leave days', value: k.leaveDays ?? 0, sub: 'Approved leave taken' },
  ];

  const chartRows = useMemo(
    () => rows.slice().sort((a, b) => (a.attendancePercent ?? 0) - (b.attendancePercent ?? 0)).slice(0, 15)
      .map((r) => ({ name: r.userName, attendance: Number(r.attendancePercent ?? 0) })),
    [rows],
  );

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[300px]">
          <DateRangeFilter value={range} preset={preset} onChange={(r, p) => { setRange(r); setPreset(p); }} />
        </div>
        <div className="pt-4"><PrintButton /></div>
      </div>

      <KpiStrip items={kpis} loading={loading} />

      <ChartPanel title="Lowest attendance first">
        {loading || chartRows.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-16">{loading ? 'Loading' : 'Nobody had working days in the selected range'}</p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(200, chartRows.length * 34)}>
            <BarChart data={chartRows} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" horizontal={false} />
              <XAxis type="number" domain={[0, 100]} unit="%" {...axisProps} />
              <YAxis type="category" dataKey="name" {...axisProps} width={150} />
              <Tooltip {...tooltipStyle} formatter={(v) => [`${v}%`, 'Attendance']} />
              <Bar dataKey="attendance" radius={[0, 4, 4, 0]} barSize={16}>
                {chartRows.map((_, i) => <Cell key={i} fill={barColor(i)} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartPanel>

      <ReportTable
        title="Attendance per person"
        columns={COLUMNS}
        rows={rows}
        csvName={`attendance-summary-${range.from}-to-${range.to}`}
        emptyText="Nobody had working days in the selected range"
        footnote="Working days are counted up to today, and follow each person's own hours. Holidays and weekly offs are not working days."
      />
    </div>
  );
};

export default AttendanceSummaryReport;
